// ═══════════════════════════════════════════════════════════════════
//  SheetLinkBadge — Visual + click affordance for sheet-link blocks.
// ═══════════════════════════════════════════════════════════════════
//
//  Rendered inside `<WorkflowBlock>` when `isSheetLinkBlock(block)` is
//  true. Phase 4 of ATLAS_COMPLETION_PLAN.md (steps 31-34, 38).
//
//  Behaviour:
//    - Top-left overlay icon (Link2 / AlertTriangle on broken link)
//    - Hover → tooltip showing target doc/sheet/block
//    - Click → navigate to /atlas/doc/<docId>[/sheet/<sheetId>][?block=<blockId>]
//    - Click does NOT propagate to block's drag/select handlers
//
//  Cross-tier: this component is plain UI; the tier-gate is enforced
//  by the route at navigation-time (AtlasDoc shows AtlasUpsellCard
//  for !atlasEnabled users).
//
// ═══════════════════════════════════════════════════════════════════

import { useNavigate } from 'react-router-dom';
import { Link2, AlertTriangle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { WorkflowBlock } from '@/types/workflow';
import type { SheetLinkTarget } from '@/types/atlas';
import { getLinkTarget } from '@/lib/sheetLink';
import { logger } from '@/lib/logger';

export interface SheetLinkBadgeProps {
  block: WorkflowBlock;
}

/** Build the route URL for navigating to a sheet-link's target. */
export function sheetLinkTargetUrl(target: SheetLinkTarget): string {
  let url = `/atlas/doc/${encodeURIComponent(target.documentId)}`;
  if (target.sheetId) {
    url += `/sheet/${encodeURIComponent(target.sheetId)}`;
  }
  if (target.blockId) {
    url += `?block=${encodeURIComponent(target.blockId)}`;
  }
  return url;
}

export function SheetLinkBadge({ block }: SheetLinkBadgeProps) {
  const navigate = useNavigate();
  const target = getLinkTarget(block.config);
  const isBroken = target === null;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!target) {
      logger.warn('atlas_sheet_link_broken_click', { blockId: block.id });
      return;
    }
    logger.info('atlas_sheet_link_clicked', {
      blockId: block.id,
      targetDocumentId: target.documentId,
      targetSheetId: target.sheetId,
      targetBlockId: target.blockId,
    });
    navigate(sheetLinkTargetUrl(target));
  };

  const Icon = isBroken ? AlertTriangle : Link2;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={handleClick}
          // Prevent block-drag while clicking the badge.
          onMouseDown={(e) => e.stopPropagation()}
          aria-label={
            isBroken ? 'Broken sheet link' : `Open linked atlas document`
          }
          data-testid="sheet-link-badge"
          data-broken={isBroken || undefined}
          className={
            // Layered above the standard block content; positioned via
            // absolute so it doesn't disturb the block's flex layout.
            'absolute top-1.5 left-1.5 w-6 h-6 rounded-md flex items-center ' +
            'justify-center transition-colors z-10 ' +
            (isBroken
              ? 'text-destructive hover:bg-destructive/10'
              : 'text-primary hover:bg-primary/10')
          }
        >
          <Icon className="w-3.5 h-3.5" aria-hidden="true" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" align="start" className="max-w-xs">
        {isBroken ? (
          <BrokenLinkContent />
        ) : (
          <LinkPreviewContent target={target} />
        )}
      </TooltipContent>
    </Tooltip>
  );
}

function LinkPreviewContent({ target }: { target: SheetLinkTarget }) {
  return (
    <div className="space-y-1">
      <div className="font-medium text-xs">Sheet link → atlas document</div>
      <div className="text-[11px] text-muted-foreground space-y-0.5">
        <div>
          <span className="opacity-70">Doc:</span>{' '}
          <code className="text-[10px]">{target.documentId}</code>
        </div>
        {target.sheetId && (
          <div>
            <span className="opacity-70">Sheet:</span>{' '}
            <code className="text-[10px]">{target.sheetId}</code>
          </div>
        )}
        {target.blockId && (
          <div>
            <span className="opacity-70">Block:</span>{' '}
            <code className="text-[10px]">{target.blockId}</code>
          </div>
        )}
      </div>
      <div className="text-[10px] text-muted-foreground pt-1 border-t mt-1">
        Click to open in /atlas
      </div>
    </div>
  );
}

function BrokenLinkContent() {
  return (
    <div className="space-y-1">
      <div className="font-medium text-xs text-destructive">Broken sheet link</div>
      <div className="text-[11px] text-muted-foreground">
        Block has type <code>sheet-link</code> but <code>linkTarget</code> is
        missing or malformed. Edit the block to set a valid target.
      </div>
    </div>
  );
}
